<template>
  <div class="flex flex-col gap-3 proxima_nova">
    <span class="text-[18px] font-semibold text_color">Default Country code</span>

    <div class="form_col w-72 pb-4">
      <a-select
        v-model:value="selectedCountry"
        showSearch
        :loading="countriesLoading"
        :filter-option="filterCountryOption"
        placeholder="Select default country code"
        @change="handleChange"
      >
        <template #suffixIcon>
          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
            class="size-4"
          />
        </template>
        <a-select-option
          v-for="country in countryDialOptions"
          :key="`country_${country.cca2}`"
          :value="country.cca2"
          :label="`${country.label} (${country.dialCode})`"
        >
          <span class="flex items-center gap-2">
            <img
              v-if="country.flag"
              :src="country.flag"
              :alt="country.label"
              class="country_flag"
            />
            <span>{{ country.label }} ({{ country.dialCode }})</span>
          </span>
        </a-select-option>
      </a-select>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import { fetchCountries } from "@/src/utils/utils.js";
import { getCallingCodeByCountry } from "@/src/utils/phoneNumberUtils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";

const store = useStore();

const rawCountries = ref([]);
const countriesLoading = ref(false);
const selectedCountry = ref(null);
const isSaving = ref(false);

const workspaceCountryCode = computed(
  () => store.getters["workspace/workspaceDefaultCountryCode"] || "US",
);

const workspaceDialCode = computed(
  () => store.getters["workspace/workspaceDefaultDialCode"] || "+1",
);

const countryDialOptions = computed(() => {
  return rawCountries.value
    .map((country) => {
      const callingCode = getCallingCodeByCountry(country.countryCode);
      if (!callingCode) return null;
      return {
        label: country.label,
        cca2: country.countryCode,
        dialCode: `+${callingCode}`,
        flag: country.flag,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label));
});

const filterCountryOption = (input, option) => {
  const label = option.label || "";
  return label.toString().toLowerCase().includes(input.toLowerCase());
};

const handleChange = async (cca2) => {
  if (!cca2 || isSaving.value) return;
  const country = countryDialOptions.value.find((c) => c.cca2 === cca2);
  if (!country) return;
  if (country.dialCode === workspaceDialCode.value) return;

  try {
    isSaving.value = true;
    const response = await store.dispatch(
      "workspace/updateWorkspaceDefaultCountryCode",
      country.dialCode,
    );
    if (response?.success) {
      message.success("Default country code updated");
    } else {
      message.error(response?.error || "Failed to update default country code");
    }
  } catch (error) {
    console.error("Error updating default country code:", error);
    message.error("Failed to update default country code");
  } finally {
    isSaving.value = false;
  }
};

watch(workspaceCountryCode, (val) => {
  if (val) selectedCountry.value = val;
});

onMounted(async () => {
  await Promise.all([
    fetchCountries(rawCountries, countriesLoading),
    store.dispatch("workspace/fetchDefaultCountryCode"),
  ]);
  selectedCountry.value = workspaceCountryCode.value;
});
</script>

<style scoped>
.country_flag {
  width: 18px;
  height: 13px;
  object-fit: cover;
  border-radius: 2px;
  flex-shrink: 0;
}
</style>
